import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactNode } from 'react';
import {
  LayoutDashboard,
  Zap,
  Monitor,
  Radio,
  Settings,
  Database,
  Puzzle,
  Download,
  LogOut,
} from 'lucide-react';
import { signOut, useSession } from '../lib/auth-client';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  SidebarSeparator,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@databro/ui';
import { useExtensionStatus } from '../hooks/useExtensionStatus';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Actions', href: '/actions', icon: Zap },
  { name: 'Recordings', href: '/recordings', icon: Radio },
  { name: 'Clients', href: '/clients', icon: Monitor },
];

const secondaryNavigation = [
  { name: 'Install Extension', href: '/install', icon: Download },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const extensionStatusConfig = {
  checking: {
    color: 'bg-yellow-500',
    label: 'Checking...',
    description: 'Checking extension connection',
  },
  connected: {
    color: 'bg-green-500',
    label: 'Connected',
    description: 'Chrome extension is connected',
  },
  disconnected: {
    color: 'bg-red-500',
    label: 'Disconnected',
    description: 'Chrome extension not detected',
  },
  not_configured: {
    color: 'bg-muted-foreground/50',
    label: 'Not Configured',
    description: 'Extension ID not configured',
  },
} as const;

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { status: extensionStatus } = useExtensionStatus();
  const { data: session } = useSession();
  const statusConfig = extensionStatusConfig[extensionStatus];

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon" className="border-r-0">
        <SidebarHeader className="p-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-sm">
              <Database className="size-5" />
            </div>
            <span className="text-lg font-semibold tracking-tight group-data-[collapsible=icon]:hidden">
              DataBro
            </span>
          </Link>
        </SidebarHeader>
        <SidebarSeparator className="opacity-50" />
        <SidebarContent className="px-2">
          <SidebarGroup className="py-4">
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.name}
                        className="h-10 px-3"
                      >
                        <Link to={item.href}>
                          <item.icon className="size-[18px]" />
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <SidebarGroup className="mt-auto pb-4">
            <SidebarGroupContent>
              <SidebarMenu>
                {secondaryNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.name}
                        className="h-10 px-3"
                      >
                        <Link to={item.href}>
                          <item.icon className="size-[18px]" />
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="gap-1 p-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={extensionStatus === 'connected' ? '/clients' : '/install'}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-accent"
              >
                <div className="relative flex-shrink-0">
                  <Puzzle className="size-5 text-muted-foreground" />
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-sidebar ${statusConfig.color}`}
                  />
                </div>
                <div className="flex flex-col group-data-[collapsible=icon]:hidden">
                  <span className="font-medium leading-none">Extension</span>
                  <span className="mt-0.5 text-xs text-muted-foreground">
                    {statusConfig.label}
                  </span>
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col">
              <span className="font-medium">{statusConfig.label}</span>
              <span className="text-xs text-muted-foreground">
                {statusConfig.description}
              </span>
            </TooltipContent>
          </Tooltip>
          {session?.user && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent"
                >
                  <LogOut className="size-5 flex-shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-medium leading-none">
                      {session.user.name || session.user.email}
                    </span>
                    <span className="mt-0.5 text-xs text-muted-foreground">
                      Sign out
                    </span>
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="flex flex-col">
                <span className="font-medium">Sign out</span>
                <span className="text-xs text-muted-foreground">
                  {session.user.email}
                </span>
              </TooltipContent>
            </Tooltip>
          )}
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">DataBro</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {location.pathname !== '/' && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      {navigation.find((n) => n.href === location.pathname)
                        ?.name ||
                        secondaryNavigation.find(
                          (n) => n.href === location.pathname
                        )?.name ||
                        'Page'}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
          </Breadcrumb>
        </header>
        <main className="flex-1 overflow-auto p-4 md:p-6">
          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
